import { createStore } from 'zustand/vanilla';
import { subscribeWithSelector, persist, devtools } from 'zustand/middleware';
import {
    type AuthenticateConfig,
    type Balance,
    type PlayResponse,
    type Round,
} from 'stake-engine';
import type { AuthenticateData, GameMode, SpinMode } from '../utils/api.types';

const VALIDATOR = 1_000_000;

export const apiStore = createStore<Store>()(
    devtools(
        subscribeWithSelector(
            persist(
                (set, get) => ({
                    state: 'loading',
                    balance: { amount: 0, currency: 'USD' },
                    config: {
                        minBet: 0,
                        maxBet: 0,
                        stepBet: 0,
                        defaultBetLevel: 0,
                        betLevels: [],
                    },
                    betAmount: 0,
                    round: null,
                    roundActive: false,
                    freeSpinsActive: false,
                    actions: {
                        updateGameState: (state: GameState) => {
                            set({ state });
                        },
                        setFreeSpinsActive: (freeSpinsActive: boolean) => {
                            set({ freeSpinsActive });
                        },
                        setPlayResponse: (event: Partial<PlayResponse>) => {
                            const validatedEvent = validateNumbers(event);

                            set({
                                round: validatedEvent.round || null,
                                roundActive: !!validatedEvent.round?.active,
                                balance: nextBalance(get().balance, validatedEvent.balance),
                            });
                        },
                        /**
                         * Balance on its own — from the end round response or the
                         * balance poll. Takes a raw RGS amount, like every other
                         * response the store is fed.
                         */
                        setBalance: (balance: Balance) => {
                            set({ balance: nextBalance(get().balance, parseBalance(balance)) });
                        },
                        // called after EndRound is attempted — successful or not — so a
                        // round that the server already settled can't retrigger EndRound
                        // forever; a genuinely open round is still recovered by the play
                        // request's round-already-active fallback
                        markRoundEnded: () => {
                            set({ roundActive: false });
                        },
                        setAuthenticateResponse: (event: Partial<AuthenticateData>) => {
                            const validatedEvent = validateNumbers(event);

                            set({
                                round: validatedEvent.round || null,
                                roundActive: !!validatedEvent.round?.active,
                                balance: nextBalance(get().balance, validatedEvent.balance),
                                config: validatedEvent.config as AuthenticateConfig,
                                gameModes: validatedEvent.gameModes,
                            });

                            // Seed (or self-heal) the bet amount: re-seed whenever the
                            // current/persisted value isn't a valid bet level, which also
                            // recovers from stale localStorage values.
                            const betLevels = validatedEvent.config?.betLevels ?? [];
                            if (!betLevels.includes(get().betAmount ?? 0)) {
                                set({
                                    betAmount: validatedEvent.config?.defaultBetLevel,
                                });
                            }
                        },
                        betUp: () => {
                            set((state) => {
                                const levels = state.config?.betLevels;
                                if (!levels?.length) return {};
                                const idx = levels.indexOf(state.betAmount || 0);
                                const next = idx === -1 ? levels[0] : levels[idx + 1];
                                return next !== undefined ? { betAmount: next } : {};
                            });
                        },
                        betDown: () => {
                            set((state) => {
                                const levels = state.config?.betLevels;
                                if (!levels?.length) return {};
                                const idx = levels.indexOf(state.betAmount || 0);
                                const next = idx === -1 ? levels[0] : levels[idx - 1];
                                return next !== undefined ? { betAmount: next } : {};
                            });
                        },
                    },
                }),
                { name: 'game-storage', partialize: (state) => ({ betAmount: state.betAmount }) },
            ),
        ),
    ),
);

function validateNumbers(data: Partial<AuthenticateData>): Partial<AuthenticateData> {
    return {
        ...data,
        ...(data.balance && { balance: parseBalance(data.balance) }),
    };
}

/** Raw RGS amount (1_000_000 -> 1.00) to the displayable amount. */
function parseBalance(balance: Balance): Balance {
    return { ...balance, amount: balance.amount / VALIDATOR };
}

/**
 * Keeps the current balance object when the value hasn't changed. The balance is
 * re-requested every second, and subscribers compare by reference — a fresh
 * object holding the same amount would replay the balance change animation on
 * every poll.
 */
function nextBalance(current: Balance, incoming?: Balance): Balance {
    if (!incoming) return current;

    return current.amount === incoming.amount && current.currency === incoming.currency
        ? current
        : incoming;
}

type Store = Partial<AuthenticateData> & {
    state: GameState;
    betAmount?: number;
    balance: Balance;
    config: AuthenticateConfig;
    /** Bet modes the RGS offers, including the features it sells (see {@link getBuyMode}). */
    gameModes?: GameMode[];
    round: Round | null;
    /** The backend round is still open and must be ended before the next play. */
    roundActive: boolean;
    /** The free spins feature is running (set while playing out the round's book). */
    freeSpinsActive: boolean;
    actions: {
        updateGameState: (state: GameState) => void;
        setFreeSpinsActive: (active: boolean) => void;
        setAuthenticateResponse: (event: Partial<AuthenticateData>) => void;
        setPlayResponse: (event: Partial<PlayResponse>) => void;
        setBalance: (balance: Balance) => void;
        markRoundEnded: () => void;
        betUp: () => void;
        betDown: () => void;
    };
};

export type GameState = 'loading' | 'idle' | 'spin' | 'reveal';

export function setGameState(state: GameState) {
    apiStore.getState().actions.updateGameState(state);
}

export function setFreeSpinsActive(active: boolean) {
    apiStore.getState().actions.setFreeSpinsActive(active);
}

export function isFreeSpinsActive(): boolean {
    return apiStore.getState().freeSpinsActive;
}

/** The bet the next play request will be placed at, in raw RGS units. */
export function getBetAmount(): number {
    return apiStore.getState().betAmount ?? 0;
}

/** Bet mode for the next play request: free spins take over from the base game. */
export function getSpinMode(): SpinMode {
    return isFreeSpinsActive() ? 'FREESPINS' : 'BASE';
}

/**
 * The feature this game sells, or undefined when it sells nothing. Read off the
 * authenticate response rather than hardcoded, so the mode played and the price
 * charged are always the ones the RGS reported.
 *
 * The buyable mode is picked by its price, not by its name: the base game is the
 * one charged at the plain bet, so any mode with a different cost multiplier is
 * a feature that can be bought. Names are the RGS's own and vary per game, which
 * is why they are not matched against.
 */
export function getBuyMode(): GameMode | undefined {
    return apiStore.getState().gameModes?.find((gameMode) => gameMode.costMultiplier !== 1);
}

/** What buying a mode costs at the current bet, in raw RGS units. */
export function getBuyCost(gameMode: GameMode): number {
    return getBetAmount() * gameMode.costMultiplier;
}

export function isIdle(): boolean {
    return apiStore.getState().state === 'idle';
}
